import React, { useEffect, useRef, useState } from 'react';
import { PopoverShell, PopoverMenuItem, Divider, TooltipTrigger } from '@spark';
import { isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';

// Click-triggered dropdown replacing antd <Dropdown menu={...} trigger={['click']}>.
// Consumes the same `{ items, onClick }` menu object built by
// ResponseInterpreterSection.buildNerDropdownMenu (including group / divider
// entries) and forwards `{ key, domEvent }` to the menu's onClick so the
// existing assignment logic stays untouched.
const NerMenuDropdown = ({ menu, children }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const handleItemClick = (key, e) => {
        e?.stopPropagation?.();
        setOpen(false);
        menu?.onClick?.({ key, domEvent: e });
    };

    const renderEntry = (entry, idx) => {
        if (entry?.type === 'divider') {
            return <Divider key={`divider-${idx}`} />;
        }
        if (entry?.type === 'group') {
            return (
                <div key={entry.key ?? `group-${idx}`}>
                    <div
                        style={{
                            padding: '4px 12px',
                            fontSize: 12,
                            fontWeight: 500,
                            color: 'var(--semantic-content-secondary, #8c8c8c)',
                        }}
                    >
                        {entry.label}
                    </div>
                    {(entry.children || []).map((child) => (
                        <PopoverMenuItem
                            key={child.key}
                            label={child.label}
                            onClick={(e) => handleItemClick(child.key, e)}
                        />
                    ))}
                </div>
            );
        }
        return (
            <PopoverMenuItem
                key={entry?.key ?? `item-${idx}`}
                label={entry?.label}
                onClick={(e) => handleItemClick(entry?.key, e)}
            />
        );
    };

    return (
        <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
            <span onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
                {children}
            </span>
            {open && (
                <div
                    style={{ position: 'absolute', left: 0, top: 'calc(100% + 4px)', zIndex: 1400 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <PopoverShell tip={false} panelClassName="spark-popover-menu-panel spark-popover-menu-panel-regular">
                        <div
                            className="spark-popover-menu-list optune-scrollbar"
                            role="menu"
                            style={{ minWidth: 180, maxHeight: 280, overflowY: 'auto' }}
                        >
                            {(menu?.items || []).map(renderEntry)}
                        </div>
                    </PopoverShell>
                </div>
            )}
        </span>
    );
};

const JsonToken = ({
    displayValue,
    isSelected,
    onClick,
    isNerJsonMode,
    assignedField,
    onRemoveAssignment,
    nerMeta,
    buildNerDropdownMenu,
    taskType,
    readOnly = false,
}) => {
    const cleanedDisplayValue = displayValue.replace(/"/g, '');

    const classificationOrSummaryTokenContent = isSelected ? (
        // Avoid using AntD Tag here because it forces long values into a single unwrapped line.
        // Keep the visual JSON rendering intact while still showing a "selected" state + allowing clear.
        <span className="json-token__selected-pill">
            <span className="json-token__selected-value">{displayValue}</span>
            {!readOnly && (
                <span
                    className="json-token__selected-close"
                    role="button"
                    // Mouse-only: the token itself is the Tab stop and Enter on it
                    // toggles the selection off, so a second stop would be noise.
                    tabIndex={-1}
                    aria-label="Clear selection"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClick(); // Toggle off
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            onClick();
                        }
                    }}
                >
                    ×
                </span>
            )}
        </span>
    ) : (
        displayValue
    );

    if (isNerJsonMode) {
        const nerTokenContent = assignedField ? (
            // Same pill markup as the classification/summary selected state so
            // long values wrap and the clear affordance doesn't toggle the
            // dropdown (the close click stops propagation).
            <span className="json-token__selected-pill">
                <span className="json-token__selected-value">{cleanedDisplayValue || '—'}</span>
                {!readOnly && (
                    <span
                        className="json-token__selected-close"
                        role="button"
                        tabIndex={0}
                        aria-label="Remove assignment"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onRemoveAssignment(assignedField.fieldKey);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                onRemoveAssignment(assignedField.fieldKey);
                            }
                        }}
                    >
                        ×
                    </span>
                )}
            </span>
        ) : (
            displayValue
        );
        if (readOnly) {
            return (
                <span className={`json-token json-token--read-only ${assignedField ? 'is-selected' : ''}`}>
                    {nerTokenContent}
                </span>
            );
        }
        const tooltipTitle = assignedField
            ? `${assignedField.fieldLabel}`
            : 'Click to select';
        return (
            <TooltipTrigger text={tooltipTitle}>
                <NerMenuDropdown menu={buildNerDropdownMenu(nerMeta)}>
                    <span className={`json-token ${assignedField ? 'is-selected' : ''}`}>
                        {nerTokenContent}
                    </span>
                </NerMenuDropdown>
            </TooltipTrigger>
        );
    }

    if (readOnly) {
        return (
            <span className={`json-token json-token--read-only ${isSelected ? 'is-selected' : ''}`}>
                {classificationOrSummaryTokenContent}
            </span>
        );
    }

    return (
        <TooltipTrigger text={isGenerativeTask(taskType) ? `Select this as a ${getGenerativeOutputNoun(taskType)}` : "Select this as a label"}>
            <span
                className={`json-token ${isSelected ? 'is-selected' : ''}`}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={onClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick();
                    }
                }}
            >
                {classificationOrSummaryTokenContent}
            </span>
        </TooltipTrigger>
    );
};

const JsonRow = ({
    name,
    value,
    pathKey,
    fieldPath,
    isNerJsonMode,
    buildNerDropdownMenu,
    toggleJsonSelection,
    renderJsonValue,
    taskType,
    readOnly = false,
}) => {
    const keyTooltipTitle = isNerJsonMode ? 'Click to select' : isGenerativeTask(taskType) ? `Select this as a ${getGenerativeOutputNoun(taskType)}` : 'Select this as a label';

    if (readOnly) {
        return (
            <div className="json-row">
                <span>{`"${name}"`}</span>
                <span>: </span>
                {renderJsonValue}
            </div>
        );
    }

    const keyInteractiveNode = isNerJsonMode ? (
        <NerMenuDropdown
            menu={buildNerDropdownMenu({
                fieldPath: fieldPath,
                label: name,
                value: value,
            })}
        >
            <span className="json-token">
                "{name}"
            </span>
        </NerMenuDropdown>
    ) : (
        <span
            className="json-token"
            role="button"
            tabIndex={0}
            onClick={() => toggleJsonSelection(
                `${pathKey}.${name}`,
                `"${name}"`,
                fieldPath
            )}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleJsonSelection(`${pathKey}.${name}`, `"${name}"`, fieldPath);
                }
            }}
        >
            "{name}"
        </span>
    );

    return (
        <div className="json-row">
            <TooltipTrigger text={keyTooltipTitle}>
                {keyInteractiveNode}
            </TooltipTrigger>
            <span>: </span>
            {renderJsonValue}
        </div>
    );
};

const JsonTreeViewer = ({
    data,
    isNerJsonMode,
    selectedJsonTokens,
    nerPathAssignments,
    toggleJsonSelection,
    handleRemoveNerAssignment,
    buildNerDropdownMenu,
    jsonFieldMetaMap,
    getLabelFromFieldPath,
    taskType,
    readOnly = false,
}) => {

    const renderValue = (value, level, pathKey, fieldPath, taskType) => {
        const paddingLeft = level * 16;

        if (Array.isArray(value)) {
            // Whole-array selection (single-label "Entire list" mode): the
            // array node itself carries the token, so the entire list —
            // brackets included — renders as one selected tag that matches
            // the Label value pill.
            const arrayToken = !isNerJsonMode ? selectedJsonTokens?.[pathKey] : undefined;
            if (arrayToken) {
                return (
                    <div style={{ paddingLeft }}>
                        <JsonToken
                            displayValue={arrayToken}
                            isSelected
                            onClick={() => toggleJsonSelection(pathKey, arrayToken, fieldPath)}
                            isNerJsonMode={false}
                            assignedField={null}
                            onRemoveAssignment={handleRemoveNerAssignment}
                            nerMeta={null}
                            buildNerDropdownMenu={buildNerDropdownMenu}
                            taskType={taskType}
                            readOnly={readOnly}
                        />
                    </div>
                );
            }
            return (
                <div style={{ paddingLeft }}>
                    {'['}
                    {value.map((item, index) => (
                        <div key={`${pathKey}.${index}`} style={{ paddingLeft: 16 }}>
                            {renderValue(
                                item,
                                level + 1,
                                `${pathKey}.${index}`,
                                fieldPath ? `${fieldPath}[${index}]` : `[${index}]`,
                                taskType
                            )}
                            {index < value.length - 1 ? ',' : ''}
                        </div>
                    ))}
                    <div style={{ paddingLeft }}>{']'}</div>
                </div>
            );
        }

        if (typeof value === 'object' && value !== null) {
            const entries = Object.entries(value);
            return (
                <div style={{ paddingLeft }}>
                    {'{'}
                    {entries.map(([key, val], idx) => {
                        const childFieldPath = fieldPath ? `${fieldPath}.${key}` : key;
                        return (
                            <React.Fragment key={`${pathKey}.${key}`}>
                                <JsonRow
                                    name={key}
                                    value={val}
                                    pathKey={pathKey}
                                    fieldPath={childFieldPath}
                                    isNerJsonMode={isNerJsonMode}
                                    buildNerDropdownMenu={buildNerDropdownMenu}
                                    toggleJsonSelection={toggleJsonSelection}
                                    renderJsonValue={renderValue(
                                        val,
                                        level + 1,
                                        `${pathKey}.${key}`,
                                        childFieldPath,
                                        taskType
                                    )}
                                    taskType={taskType}
                                    readOnly={readOnly}
                                />
                                {idx < entries.length - 1 ? ',' : ''}
                            </React.Fragment>
                        );
                    })}
                    <div style={{ paddingLeft }}>{'}'}</div>
                </div>
            );
        }

        // Primitive value
        const isSelected = !!selectedJsonTokens[pathKey];
        const displayValue = typeof value === 'string' ? `"${value}"` : (value === null ? 'null' : String(value));
        const effectiveFieldPath = fieldPath || pathKey;

        // Normalize path logic duplicated from ResponseInterpreterSection for now, ideally should be passed in
        const normalizeFieldPath = (path) => {
            if (!path) return '';
            return path.replace(/\[\d+\]/g, '').replace(/^\./, '').replace(/\.$/, '');
        };

        const normalizedEffectivePath = normalizeFieldPath(effectiveFieldPath);
        const metaInfo = jsonFieldMetaMap?.[normalizedEffectivePath || effectiveFieldPath] || {};

        const nerMeta = {
            fieldPath: normalizedEffectivePath || effectiveFieldPath,
            label: effectiveFieldPath ? getLabelFromFieldPath(effectiveFieldPath) : '',
            value,
            arrayPath: metaInfo.arrayPath ?? null,
        };

        const assignedField = nerPathAssignments?.[normalizedEffectivePath] || nerPathAssignments?.[`root.${normalizedEffectivePath}`];

        return (
            <JsonToken
                displayValue={displayValue}
                isSelected={isSelected}
                onClick={() => toggleJsonSelection(pathKey, displayValue, fieldPath)}
                isNerJsonMode={isNerJsonMode}
                assignedField={assignedField}
                onRemoveAssignment={handleRemoveNerAssignment}
                nerMeta={nerMeta}
                buildNerDropdownMenu={buildNerDropdownMenu}
                taskType={taskType}
                readOnly={readOnly}
            />
        );
    };

    return (
        <div
            className={`json-viewer optune-scrollbar${readOnly ? ' json-viewer--read-only' : ''}`}
            style={{ maxHeight: '400px', overflow: 'auto' }}
        >
            {renderValue(data, 0, 'root', '', taskType)}
        </div>
    );
};

export default JsonTreeViewer;
